import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Stack } from '@mui/material';

import { AppDrawer, AppDrawerHeader } from '@common/ui/AppDrawer';
import { Loader } from '@common/ui/Loader';
import { ErrorAlert } from '@common/ui/ErrorAlert';
import { REQUEST_PARAM } from '@common/constants';

import { AccessStatus, useGetAccessRequest } from '@client-app-sdk';

import TaskOverview from '../components/TaskOverview';
import RequestApproval from './RequestApproval';
import AccessGroups from '@MyRequests/organisms/AccessGroups';

const ACTIVITY_DRAWER_WIDTH = 750;

export default function TaskDrawer() {
  const [queryParams, setQueryParams] = useSearchParams();

  const requestId = useMemo(() => queryParams.get(REQUEST_PARAM), [queryParams]);

  const handleClose = () => {
    queryParams.delete(REQUEST_PARAM);
    setQueryParams(queryParams);
  };

  if (!requestId) {
    return null;
  }

  return <RenderTaskDrawer requestId={requestId} open={!!requestId} onClose={handleClose} />;
}

function RenderTaskDrawer({ requestId, open, onClose }: { requestId: string; open: boolean; onClose: () => void }) {
  const { data, isFetched, error } = useGetAccessRequest(requestId, 10 * 1000);

  const drawerHeader = useMemo(
    () => <AppDrawerHeader title={`Request ${requestId} details`} onCloseClick={onClose} />,
    [onClose, requestId],
  );

  const drawerContent = useMemo(() => {
    if (!isFetched) {
      return <Loader />;
    }

    if (error) {
      return <ErrorAlert error={error} />;
    }

    if (!data) {
      return <Alert severity="error">Request not found</Alert>;
    }

    return (
      <Stack spacing={3}>
        <TaskOverview request={data} />
        <AccessGroups groups={data.access_groups} canSeeDetails={false} />
      </Stack>
    );
  }, [data, error, isFetched]);

  const drawerFooter = useMemo(() => {
    if (data?.status.status === AccessStatus.Pending || data?.status.status === AccessStatus.PENDING) {
      return (
        <Stack alignItems="center">
          <RequestApproval request={data} onApproval={onClose} />
        </Stack>
      );
    }
  }, [data, onClose]);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      header={drawerHeader}
      footer={drawerFooter}
      width={ACTIVITY_DRAWER_WIDTH}
      variant="temporary"
      noBackdrop
    >
      {drawerContent}
    </AppDrawer>
  );
}
